import { useMemo, useState } from 'react'
import { Shield, Plus, Pencil, Trash2 } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useRoles,
  useSections,
  useDeleteRole,
  type Role,
  type SectionDef,
} from '../hooks/useRoles'
import { RoleEditDialog } from '../components/roles/RoleEditDialog'

/** Краткое описание прав роли для колонки списка. */
function summarizePermissions(role: Role, sections: SectionDef[]): string {
  if (role.permissions?.all === true) {
    return 'Полный доступ'
  }
  const map = new Map(sections.map((s) => [s.key, s.label]))
  const granted = Object.entries(role.permissions?.sections ?? {})
    .filter(([, v]) => v?.view || v?.edit)
    .map(([key]) => map.get(key) ?? key)
  return granted.length > 0 ? granted.join(', ') : '—'
}

export function Roles() {
  const { data, isLoading, isError, refetch } = useRoles({ limit: 100 })
  const { data: sections = [] } = useSections()
  const deleteRole = useDeleteRole()

  const [editOpen, setEditOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const roles = useMemo(() => data?.data ?? [], [data])

  const openCreate = () => {
    setSelectedRole(null)
    setEditOpen(true)
  }

  const openEdit = (role: Role) => {
    setSelectedRole(role)
    setEditOpen(true)
  }

  const confirmDelete = async () => {
    if (!roleToDelete) return
    setDeleteError(null)
    try {
      await deleteRole.mutateAsync(roleToDelete.id)
      setRoleToDelete(null)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: unknown }).message)
          : 'Не удалось удалить роль'
      setDeleteError(msg)
    }
  }

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-tco-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Управление ролями</h1>
            <p className="text-sm text-gray-500">
              Настройка прав доступа для пользователей
            </p>
          </div>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Создать роль
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Роли ({roles.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable />
          ) : isError ? (
            <ErrorState
              title="Не удалось загрузить роли"
              onAction={() => void refetch()}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    <TableHead>Права доступа</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-center text-muted-foreground py-8"
                      >
                        Роли не найдены
                      </TableCell>
                    </TableRow>
                  ) : (
                    roles.map((role) => {
                      const isFullAccess = role.permissions?.all === true
                      return (
                        <TableRow key={role.id}>
                          <TableCell className="font-medium">{role.name}</TableCell>
                          <TableCell className="text-muted-foreground max-w-xl truncate">
                            {summarizePermissions(role, sections)}
                          </TableCell>
                          <TableCell className="text-right">
                            {isFullAccess ? (
                              <span className="text-xs text-muted-foreground">
                                Системная роль
                              </span>
                            ) : (
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Редактировать"
                                  onClick={() => openEdit(role)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Удалить"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => {
                                    setDeleteError(null)
                                    setRoleToDelete(role)
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Создание / редактирование */}
      <RoleEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        role={selectedRole}
        sections={sections}
      />

      {/* Подтверждение удаления */}
      <Dialog
        open={!!roleToDelete}
        onOpenChange={(o) => {
          if (!o) setRoleToDelete(null)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div>
              <DialogTitle>Удалить роль?</DialogTitle>
              <DialogDescription>
                Роль «{roleToDelete?.name}» будет удалена без возможности
                восстановления.
              </DialogDescription>
            </div>
          </DialogHeader>
          {deleteError && (
            <p className="text-sm text-red-600" role="alert">
              {deleteError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRoleToDelete(null)}
              disabled={deleteRole.isPending}
            >
              Отмена
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
              disabled={deleteRole.isPending}
            >
              {deleteRole.isPending ? 'Удаление…' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Roles
